import {
    Button,
    Row,
    Col,
    Space,
    // Tooltip,
    Segmented,
} from "antd";
import React, { useState, useEffect, useReducer, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { usePDF } from "react-to-pdf";
import { clsx } from "clsx";
import { clamp } from "lodash";
import { getOrderForm, updateOrderForm } from "../../../common/api/OrderFormClient";
import { getReport, upsertReport } from "../../../common/api/ReportClient";
import { OrderForm, OrderFormStatus } from "../../../types/OrderFormTypes";
// import { ResourceType } from "../../../types/ResourceType";
import { POCReport } from "../../../types/ReportTypes";
// import { TagPanel } from "../../tag/TagPanel";
import { canWrite } from "../../../types/AccessControlTypes";
import { Vendor } from "../../../types/VendorTypes";
import { isSuperAdmin } from "../../../types/UserGroup";
import { handleError } from "../../../common/ErrorHandling";
// import { SvgIcon } from "../../../common/components/SvgIcon";
import { PdfPage } from "../../../common/components/PdfPage";
import { useUserState } from "../../../common/UserContext";
import { useAppShellContext } from "../../layout/AppShell";
import { getVendors } from "../../../common/api/VendorClient";
// import { CommentsPanel } from "../../comments/CommentsPanel";
import { SummaryPanel } from "./components/SummaryPanel";
import { UtilizationMeasurePanel } from "./components/UtilizationMeasurePanel";
import { OrderFormTablePanel } from "./components/OrderFormTablePanel";
import { SkuTablePanel } from "./components/SkuTablePanel";
import OrderFormLayout from "./components/OrderFormLayout";
import { Chapter, Outline } from "./components/Outline";
import classes from "./OrderFormDetailsView.module.less";
import { sleep } from "../../../common/AsyncUtil";

enum TabKey {
    Analysis = "Analysis",
    Usage = "Usage",
}

const tabKeys = Object.values(TabKey);

const allChapters = [
    Chapter.ContractSummary,
    Chapter.NegotiationStrategy,
    Chapter.TopLegalPriorities,
];

export const OrderFormDetailsView: React.FC = () => {
    const params = useParams();
    const { me } = useUserState();
    const { getContentContainer } = useAppShellContext();
    const [disabled, setDisabled] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [orderForm, setOrderForm] = useState({} as OrderForm);
    const [orderFormId] = useState(Number(params.orderFormId) || 0);
    // const [showMessages, setShowMessages] = useState(false);
    const [vendor, setVendor] = useState({} as Vendor);
    const [, setStatusLocked] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [reload, setReload] = useState(0);
    const [report, setReport] = useState({} as POCReport);
    const [selectedChapter, setSelectedChapter] = useState<Chapter | undefined>(
        Chapter.ContractSummary
    );
    const [selectedSectionByChapter, setSelectedSectionByChapter] = useState(
        new Map(allChapters.map((chapter) => [chapter, "0"]))
    );
    const contractSummaryRef = useRef<HTMLDivElement>(null);
    const negotiationStrategyRef = useRef<HTMLDivElement>(null);
    const topLegalPriorityRef = useRef<HTMLDivElement>(null);
    const [activeChapters, setActiveChapters] = useState(allChapters);
    const [outlineOpen, setOutlineOpen] = useState(true);
    const _animating = useRef<boolean>(false);
    const [forceUpdate, _doForceUpdate] = useReducer((x) => x + 1, 0);

    const defaultTab = TabKey.Analysis;

    const [tab, setTab] = useState(defaultTab);
    const { toPDF, targetRef } = usePDF();

    const refMap = new Map([
        [Chapter.ContractSummary, contractSummaryRef],
        [Chapter.NegotiationStrategy, negotiationStrategyRef],
        [Chapter.TopLegalPriorities, topLegalPriorityRef],
    ]);

    const sumbmitOrderForm = async (orderForm: OrderForm) => {
        await updateOrderForm(orderForm);
        setReload(reload + 1);
    };

    const loadVendor = async (vendorKey: string) => {
        try {
            const vendors = await getVendors([vendorKey]);
            if (vendors.length) {
                setVendor(vendors[0]);
            }
        } catch (error) {
            handleError(error);
        }
    };

    const loadReport = async () => {
        if (!orderFormId) return;
        try {
            const data = await getReport(orderFormId);
            setReport(data);
        } catch (error) {
            handleError(error);
        }
    };

    const handleReportChange = async (report: POCReport) => {
        if (!orderFormId) return;
        await upsertReport({ ...report, orderFormId });
        loadReport();
    };

    const isAnimating = (): boolean => {
        return _animating.current;
    };

    const animateStart = () => {
        _animating.current = true;
    };

    const animateEnd = () => {
        _animating.current = false;
        _doForceUpdate();
    };

    const setSelectedSection = (chapter: Chapter, section: string): boolean => {
        const before = selectedSectionByChapter;
        const after = new Map(before);
        after.set(chapter, section);
        setSelectedSectionByChapter(after);
        return before.get(chapter) !== after.get(chapter);
    };

    const scrollToChapter = (chapter: Chapter) => {
        const el = refMap.get(chapter)?.current;
        if (el) {
            const scrollContainer = getContentContainer?.();
            if (scrollContainer) {
                scrollContainer.addEventListener("scrollend", animateEnd, { once: true });
                animateStart();
            }
            el.scrollIntoView({ behavior: "smooth" });
        }
    };

    const onChapterChange = useCallback(
        async (chapter: Chapter) => {
            setSelectedChapter(chapter);
            if (!activeChapters.includes(chapter)) {
                setActiveChapters([...activeChapters, chapter]);
                // 200 is the transition time of opening a chapter
                await sleep(200);
            }
        },
        [selectedChapter, activeChapters]
    );

    const onSectionChange = useCallback(
        async (chapter: Chapter, section: string) => {
            await Promise.all([
                // 200 is the transition time of switching sections
                setSelectedSection(chapter, section) ? sleep(200) : Promise.resolve(),
                onChapterChange(chapter),
            ]);
        },
        [selectedSectionByChapter, onChapterChange]
    );

    useEffect(() => {
        const scrollContainer = getContentContainer?.();

        const handleScroll = () => {
            if (isAnimating() || activeChapters.length === 0) {
                return;
            }

            const topZeroBottomZero = { top: 0, bottom: 0 };
            const { top: scrollTop, bottom: scrollBottom } =
                scrollContainer?.getBoundingClientRect() || topZeroBottomZero;
            const chaptersByViewportArea = allChapters
                .filter((c) => activeChapters.includes(c))
                .map((c) => {
                    const { top: elTop, bottom: elBottom } =
                        refMap.get(c)?.current?.getBoundingClientRect() || topZeroBottomZero;
                    const visibleHeight =
                        clamp(elBottom, scrollTop, scrollBottom) -
                        clamp(elTop, scrollTop, scrollBottom);
                    const maxVisibleHeight = clamp(elBottom - elTop, 1, scrollBottom - scrollTop); // avoid potential divide by zero
                    return { chapter: c, score: visibleHeight / maxVisibleHeight };
                })
                .sort((a, b) => b.score - a.score);

            setSelectedChapter(chaptersByViewportArea[0].chapter);
        };

        handleScroll();
        scrollContainer?.addEventListener("scroll", handleScroll);
        return () => {
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [activeChapters, forceUpdate]);

    const onActiveChaptersChange = useCallback(
        async (newActiveChapters: Chapter[]) => {
            animateStart();
            setActiveChapters(newActiveChapters);

            const collapsedChapters = activeChapters.filter((c) => !newActiveChapters.includes(c));
            if (selectedChapter && collapsedChapters.includes(selectedChapter)) {
                setSelectedChapter(undefined);
            }

            // 200 is the transition time of closing a chapter
            await sleep(200);
            animateEnd();
        },
        [selectedChapter, activeChapters]
    );

    useEffect(() => {
        getOrderForm(orderFormId)
            .then((order) => {
                const _readOnly = !canWrite(order.access);
                setReadOnly(_readOnly);
                setOrderForm(order.orderForm);
            })
            .catch((error) => {
                handleError(error);
                setHasError(true);
            });
    }, [reload]);

    useEffect(() => {
        const _statusLocked =
            orderForm.status === OrderFormStatus.RENEWED ||
            orderForm.status === OrderFormStatus.WILL_NOT_RENEW;
        setStatusLocked(_statusLocked);
        setDisabled(readOnly || _statusLocked);

        if (orderForm.vendorKey) {
            loadVendor(orderForm.vendorKey);
        }
    }, [orderForm]);

    useEffect(() => {
        loadReport();
    }, [orderFormId]);

    if (hasError) {
        return <></>;
    }

    const formattedReport = {
        ...report,
        contractSummaryV1: report.contractSummaryV1?.map((s) => ({
            ...s,
            key: s.key
                .replace(/<[^>]*>/g, "")
                .replace(/[\t\n\r\f\v]/g, "")
                .trim(),
        })),
    };

    const createPdf = async () => {
        try {
            await toPDF({
                page: { format: [400, targetRef.current.offsetHeight * 0.2645833333 + 1] },
                canvas: { useCORS: true },
            });
        } catch (error) {
            handleError(error);
        }
    };

    /*
     * Temporarily remove comments and tags for this version
     * */

    // const messagesDrawer = (
    //     <>
    //         {vendor.id ? (
    //             <CommentsPanel
    //                 resourceId={vendor.id}
    //                 visible={showMessages}
    //                 resourceType={ResourceType.Vendor}
    //                 onClose={() => setShowMessages(false)}
    //             />
    //         ) : null}
    //     </>
    // );
    //
    // const toolbox = (
    //     <>
    //         <Space size={10}>
    //             <Button
    //                 type="link"
    //                 size={"small"}
    //                 onClick={() => {
    //                     setShowMessages(true);
    //                 }}
    //                 icon={
    //                     <Tooltip title={"Show comments"}>
    //                         <SvgIcon name="message01" />
    //                     </Tooltip>
    //                 }
    //             ></Button>
    //         </Space>
    //     </>
    // );
    //
    // const toolPanel = (
    //     <div style={{ paddingBottom: 10, paddingTop: 10 }}>
    //         <Row justify="start" align="middle">
    //             <Col span={24}>
    //                 <TagPanel
    //                     resourceId={orderFormId}
    //                     resourceType={ResourceType.OrderForm}
    //                     readOnly={disabled}
    //                 />
    //             </Col>
    //         </Row>
    //     </div>
    // );

    const usagePanel = (
        <>
            <UtilizationMeasurePanel
                orderFormId={orderFormId}
                vendorId={vendor.id}
                utilizationScore={orderForm.utilizationScore}
                onChange={() => setReload(reload + 1)}
            />
            <SkuTablePanel orderFormId={orderFormId} />
        </>
    );

    const analysisPdfPanel = (
        <div ref={targetRef} style={{ position: "fixed", left: "-1000rem" }}>
            <PdfPage style={{ width: "400mm", height: "auto" }}>
                <SummaryPanel
                    orderFormId={orderForm.id}
                    editable={false}
                    showTab={false}
                    activeChapters={allChapters}
                    report={formattedReport}
                />
            </PdfPage>
        </div>
    );

    const analysisPanel = (
        <>
            {analysisPdfPanel}
            <SummaryPanel
                orderFormId={orderForm.id}
                style={{ marginTop: 16 }}
                editable={isSuperAdmin(me.roles || [])}
                report={formattedReport}
                onReportChange={handleReportChange}
                selectedSectionByChapter={selectedSectionByChapter}
                onSectionChange={(chapter, section) => {
                    setSelectedSection(chapter, section);
                    setSelectedChapter(chapter);
                }}
                contractSummaryRef={contractSummaryRef}
                negotiationStrategyRef={negotiationStrategyRef}
                topLegalPriorityRef={topLegalPriorityRef}
                activeChapters={activeChapters}
                onActiveChaptersChange={onActiveChaptersChange}
            />
        </>
    );

    const downloadPdfButton =
        tab === TabKey.Analysis ? (
            <Col>
                <Space>
                    <Button
                        type="link"
                        onClick={() => {
                            createPdf();
                        }}
                    >
                        Download PDF
                    </Button>
                </Space>
            </Col>
        ) : null;

    const currentPanel = tab === TabKey.Analysis ? analysisPanel : usagePanel;

    const tabSwitcher = (
        <Row style={{ marginTop: 16 }}>
            <Col flex={1}>
                <Segmented
                    options={tabKeys}
                    onChange={(value) => {
                        setTab(value as TabKey);
                    }}
                />
            </Col>
            {downloadPdfButton}
        </Row>
    );

    const showOutline = tab === TabKey.Analysis;
    const outline = (
        <Outline
            open={showOutline && outlineOpen}
            onClose={() => setOutlineOpen(false)}
            report={formattedReport}
            selectedChapter={selectedChapter}
            onChapterChange={(chapter) =>
                onChapterChange(chapter).then(() => scrollToChapter(chapter))
            }
            selectedSection={selectedChapter && selectedSectionByChapter.get(selectedChapter)}
            onSectionChange={(chapter, section) =>
                onSectionChange(chapter, section).then(() => scrollToChapter(chapter))
            }
        />
    );

    const content = (
        <div>
            {
                //messagesDrawer
                // <Row>
                //   <Col span={24}>{toolPanel}</Col>
                // </Row>
            }
            <div
                className={clsx(
                    classes.contentContainer,
                    showOutline && outlineOpen && "with-aside"
                )}
            >
                {outline}
                <div className={classes.content}>
                    <OrderFormTablePanel
                        orderFormId={orderFormId}
                        orderForm={orderForm}
                        onChange={sumbmitOrderForm}
                        disabled={disabled}
                        showExpand={showOutline && !outlineOpen}
                        onExpand={() => setOutlineOpen(true)}
                    />
                    {tabSwitcher}
                    {currentPanel}
                </div>
            </div>
        </div>
    );

    return (
        <OrderFormLayout
            currentPath={[
                {
                    name: "Procurement Hub",
                    path: "/project",
                },
                {
                    name: `${orderForm.name || ""}`,
                    path: `/order/${orderForm.id}`,
                },
            ]}
            contentPanel={content}
            toolbox={null}
        />
    );
};
